import { CLAIMS_FOOTER_TYPES } from '@/views/screens/Claims/constants';
import type { LossAndDamageSearchFilters, OverChargeSearchFilters } from '@/views/screens/Claims/types';

export const OVERCHARGE_NOTES_TYPES_OPTIONS = [
  { value: 'x', label: 'External' },
  { value: 'i', label: 'In-House' },
];

export const PRE_CLAIMS_TYPES_OPTIONS = [
  { value: 'I', label: 'In-House' },
  { value: 'E', label: 'Claim' },
];

export const CLAIMS_SHIPPER_CONSIGNEE_FETCH_ERROR_TYPE = {
  SUCCESS: 'SUCCESS',
  EMPTY: 'EMPTY',
  ERROR: 'ERROR',
  CLEAR: 'CLEAR',
};

export const LOSS_AND_DAMAGE_CLAIMS_FILTERS_INITIAL_DATA: LossAndDamageSearchFilters = {
  branchKey: undefined,
  carrierKey: undefined,
  freightBillNo: undefined,
  invoiceNo: undefined,
  claimNo: undefined,
  claimStatus: ['O', 'P', 'H', 'C'],
  daysOld: undefined,
  divisionKey: undefined,
  regionKey: undefined,
  claimType: undefined,
  consignee: undefined,
  claimProcessor: undefined,
  startDate: undefined,
  endDate: undefined,
  sortColumn: 'CreatedOn',
  sortDirection: 'DESC',
  pageSize: 10,
  pageNo: 1,
  FooterStatus: CLAIMS_FOOTER_TYPES.ALL,
};

export const LAD_CLAIM_EXPORT_FIELDS = [
  { name: 'claimNumber', label: 'Claim Number' },
  { name: 'claimProNo', label: 'Freight Bill / Pro Number' },
  { name: 'invoiceNo', label: 'Invoice Number' },
  { name: 'carrierName', label: 'Transportation Provider' },
  { name: 'consignee', label: 'Consignee' },
  { name: 'claimTotal', label: 'Amount Filed' },
  { name: 'claimPaidAmount', label: 'Amount Paid' },
  { name: 'difference', label: 'Difference Amount' },
  { name: 'claimType', label: 'Claim Type' },
  { name: 'claimStatus', label: 'Status' },
  { name: 'dateFiled', label: 'Filed Date' },
  { name: 'claimCloseDate', label: 'Closed Date' },
  { name: 'createdOn', label: 'Created On' },
  { name: 'createdByUser', label: 'Created By' },
  { name: 'modifiedOn', label: 'Modified On' },
  { name: 'modifiedByUser', label: 'Modified By' },
];

export const CREATE_LD_CLAIM_FORM_IDS = {
  claimsDetailsForm: '1',
  claimsFinancialsForm: '3',
};

export const CLAIMS_TOTAL_COUNTS_TYPES = {
  [CLAIMS_FOOTER_TYPES.ALL]: 'totalClaim',
  [CLAIMS_FOOTER_TYPES.OPEN]: 'openClaim',
  [CLAIMS_FOOTER_TYPES.CLOSED]: 'closedClaim',
  [CLAIMS_FOOTER_TYPES.HOLD]: 'holdClaim',
  ALL_FOR_OC_CLAIM: 'totalRecords',
};

export const OVERCHARGE_CLAIMS_FILTERS_INITIAL_DATA: OverChargeSearchFilters = {
  fbocKey: '',
  carrierKey: undefined,
  freightBillNo: '',
  invoiceNo: '',
  packageNo: '',
  byAge: false,
  daysOld: 0,
  sortColumn: 'FBOCKey',
  sortDirection: 'DESC',
  pageSize: 10,
  pageNo: 1,
  FooterStatus: CLAIMS_FOOTER_TYPES.ALL,
  StatusCodes: [],
  StartDate: undefined,
  EndDate: undefined,
};

export const DEFAULT_LOSS_AND_DAMAGE_CLAIMS_TABLE_VIEW_COLUMNS = [
  {
    columnName: 'Claim Status',
    columnValue: 'claimStatus',
  },
  {
    columnName: 'Claim Type',
    columnValue: 'claimType',
  },
  {
    columnName: 'Freight Bill / Pro Number',
    columnValue: 'claimProNo',
  },
  {
    columnName: 'Invoice Number',
    columnValue: 'invoiceNo',
  },
  {
    columnName: 'Transportation Provider',
    columnValue: 'carrierName',
  },
  {
    columnName: 'Consignee',
    columnValue: 'consignee',
  },
  {
    columnName: 'Amount Filed',
    columnValue: 'claimTotal',
  },
  {
    columnName: 'Amount Paid',
    columnValue: 'claimPaidAmount',
  },
  {
    columnName: 'Difference Amount',
    columnValue: 'difference',
  },
];

export const DEFAULT_OVERCHARGE_CLAIMS_TABLE_VIEW_COLUMNS = [
  {
    columnName: 'Claim Status',
    columnValue: 'claimStatusDescription',
  },
  {
    columnName: 'Overcharge Amount',
    columnValue: 'overchargeAmount',
  },
  {
    columnName: 'Recovery Amount',
    columnValue: 'recoveryAmount',
  },
  {
    columnName: 'Claim Type',
    columnValue: 'claimTypeDesc',
  },
  {
    columnName: 'Reason',
    columnValue: 'reasonCodeDesc',
  },
  {
    columnName: 'Submitted Date',
    columnValue: 'dateSubmitted',
  },
  {
    columnName: 'Closed Date',
    columnValue: 'closedDate',
  },
];

export const INTEGER_NUMBER_VALIDATION_REGEX = /^[1-9][0-9]*$/g;
export const FLOAT_NUMBER_VALIDATION_REGEX = /^(?=.*[1-9])[0-9]*[.,]?[0-9]{1,}$/g;

export const LD_CLAIM_DUPLICATE_STATUS = {
  NEW: 0,
  PRO_NUMBER: 1,
  INVOICE_NO: 2,
  BOTH: 3,
};

export const LD_CLAIM_DUPLICATE_STATUS_MESSAGE = {
  [LD_CLAIM_DUPLICATE_STATUS.PRO_NUMBER]:
    'A Claim exists with the Freight Bill / Pro Number you have entered. Do you want to save anyway?',
  [LD_CLAIM_DUPLICATE_STATUS.INVOICE_NO]:
    'A Claim exists with the Invoice Number you have entered. Do you want to save anyway?',
  [LD_CLAIM_DUPLICATE_STATUS.BOTH]:
    'A Claim exists with both the Freight Bil and Invoice Number you have entered. Do you want to save anyway?',
};

export const PRE_CLAIMS_CREATE_DEFAULT_VALUES = {
  ITEMS: [
    {
      claimKey: 0,
      itemSeq: 1,
      itemCode: '',
      itemQuantity: undefined,
      itemDesc: '',
      measure: '',
      itemUnitMeasure: '',
      fullRelFlag: '',
      dispositionMethod: '',
      value: undefined,
      smType: '',
      smFlag: '',
      smAmount: undefined,
      dirtyFlag: 'i',
      isNew: true,
      editMode: true,
    },
  ],
  NOTES: [
    {
      commentType: undefined,
      commentText: undefined,
      sequenceNo: 1,
      oldCommentType: null,
      dirtyFlag: 'i',
      isNew: true,
      editMode: true,
    },
  ],
  FILES: [
    {
      claimKey: 0,
      sequence: 1,
      fileDesc: undefined,
      fileComments: undefined,
      fileName: undefined,
      originalFileName: undefined,
      documentType: undefined,
      description: undefined,
      isNew: true,
      editMode: true,
      fileList: [],
    },
  ],
};

export const LOSS_AND_DAMAGE_CREATE_CLAIM_DEFAULT_VALUE = {
  ITEMS: [
    {
      claimKey: 0,
      itemSeq: 1,
      smAmount: undefined,
      dirtyFlag: 'i',
      isNew: true,
      editMode: true,
    },
  ],
  NOTES: [
    {
      commentType: undefined,
      commentText: undefined,
      sequenceNo: 1,
      oldCommentType: null,
      dirtyFlag: 'i',
      isNew: true,
      editMode: true,
    },
  ],
  SETTLEMENT: [
    {
      claimKey: 0,
      settlementSeq: 1,
      accountingCode: undefined,
      amount: undefined,
      dirtyFlag: 'i',
      isNew: true,
      editMode: true,
    },
  ],
  FLIES: [
    {
      claimKey: 0,
      sequence: 1,
      fileDesc: undefined,
      fileComments: undefined,
      fileName: undefined,
      originalFileName: undefined,
      documentType: undefined,
      description: undefined,
      isNew: true,
      editMode: true,
    },
  ],
};

export const CREATE_OVER_CHARGE_CLAIM_DEFAULT_VALUE = {
  ITEMS: [
    {
      fbOcKey: 0,
      dirtyFlag: 'i',
      itemID: 0,
      proNo: undefined,
      invoiceNumber: undefined,
      packageNumber: undefined,
      shipDate: undefined,
      weight: 0,
      billedAmount: 0.0,
      paidAmount: 0.0,
      correctCharges: 0.0,
      overchargeAmount: 0,
      recoveryAmount: 0.0,
      isNew: true,
      editMode: true,
    },
  ],
  NOTES: [
    {
      commentType: undefined,
      commentText: undefined,
      sequenceNo: 1,
      oldCommentType: null,
      dirtyFlag: 'i',
      isNew: true,
      editMode: true,
    },
  ],
  SETTLEMENT: [
    {
      fbocKey: 0,
      settlementSeq: 1,
      accountingCode: undefined,
      amount: 0.0,
      dirtyFlag: 'i',
      isNew: true,
      editMode: true,
    },
  ],
};
